import { type CustomValidateFunction } from '@/components/forms/types';
import type { ErrorHandlerFunction } from '@/api/generator';
import type { AggregatedAjvError } from '@/validation/createSchemaValidator';
import { I18nKey } from '@/utils/typings';
import { truthy } from '@/utils/helper';
import { AnyApiError, ApiError } from '@/errorHandling/typeGuards';
import { extractValidationErrors } from '@/errorHandling/errorUtils';
import { t } from 'i18next';

export function mapBackendErrorToAjvErrors(error: AnyApiError) {
  // Backend accesses members with capital letters in beginning, so we have to lowercase them to ensure correct association to form fields in DTO
  function camelCasePath(path: string) {
    return path
      .split('.')
      .map((s) => s[0].toLowerCase() + s.slice(1))
      .join('.');
  }

  // Only Ptw100BadRequestException declares validationErrors on the generated DTO.
  const validationErrors =
    error.name === ApiError.badRequest ? extractValidationErrors(error) : undefined;

  const errors = validationErrors
    ?.map((validationError) =>
      validationError.memberNames?.map<AggregatedAjvError>((path) => ({
        path: camelCasePath(path),
        errors: [t(validationError.errorMessage as I18nKey)],
      })),
    )
    .flat()
    .filter(truthy);

  return errors;
}

export function backendValidator<TValues, R>(
  apiCall: (
    requestParameters: TValues,
    errorHandlers: ErrorHandlerFunction<AnyApiError>[],
  ) => Promise<R>,
): CustomValidateFunction<TValues> {
  return async function validate(requestParameters: TValues) {
    let errors: AggregatedAjvError[] | undefined;

    await apiCall(requestParameters, [
      (error) => {
        errors = mapBackendErrorToAjvErrors(error);
      },
    ]);

    return errors;
  };
}
